import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Input, Popconfirm, Space, Switch, Table, Tabs, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';

import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';
import { deleteState, listStatesByActive, setStateActive } from '../services/stateService';
import type { State } from '../types/State';
import StateForm from './Form';
import { extractApiErrorMessage, openNotification } from '../../../utils';

type StatusTabKey = 'active' | 'passive';
const DEFAULT_PAGE_SIZE = 10;
const STATE_DELETE_BLOCKED_MESSAGE = 'Bu il silinemez. Çünkü bu ile bağlı ilçeler bulunmaktadır.';

export default function StateList({ isActive = true }: { isActive?: boolean }) {
  const { Text } = Typography;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('active') === 'false' ? 'passive' : 'active';

  const [data, setData] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusTab, setStatusTab] = useState<StatusTabKey>(initialStatus);
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });

  const isActiveFilter = statusTab === 'active';
  const debouncedApplySearch = useMemo(() => debounce((value: string) => setSearch(value), 300), []);

  useEffect(() => () => debouncedApplySearch.cancel(), [debouncedApplySearch]);

  const syncSearchParams = useCallback(
    (nextStatus: StatusTabKey) => {
      const next = new URLSearchParams(searchParams);
      next.set('active', String(nextStatus === 'active'));
      next.delete('countryId');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const fetchData = useCallback(async () => {
    if (!isActive) {
      return;
    }

    try {
      setLoading(true);
      const items = await listStatesByActive(isActiveFilter);
      setData(Array.isArray(items) ? items : []);
    } catch (error) {
      const message = extractApiErrorMessage(error, 'İller yüklenemedi');
      openNotification('error', 'Hata', message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [isActive, isActiveFilter]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    fetchData();
  }, [fetchData, isActive]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [statusTab, search]);

  const filteredData = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase('tr-TR');
    const items =
      normalizedSearch.length < 2
        ? data
        : data.filter((item) =>
            [item.id, item.name, item.stateCode, item.type].some((value) =>
              String(value ?? '').toLocaleLowerCase('tr-TR').includes(normalizedSearch),
            ),
          );

    return [...items].sort((a, b) =>
      String(a?.stateCode ?? '').localeCompare(String(b?.stateCode ?? ''), 'tr', { numeric: true, sensitivity: 'base' }),
    );
  }, [data, search]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredData.length / pagination.pageSize));
    if (pagination.current > maxPage) {
      setPagination((prev) => ({ ...prev, current: maxPage }));
    }
  }, [filteredData.length, pagination.current, pagination.pageSize]);

  const handleDelete = async (id?: number) => {
    if (typeof id !== 'number') return;
    try {
      setDeletingId(id);
      await deleteState(id);
      setData((prev) => prev.filter((item) => item.id !== id));
      openNotification('success', 'Başarılı', 'İl silindi');
    } catch (error) {
      const message = extractApiErrorMessage(error, 'İl silinemedi');
      if (message === STATE_DELETE_BLOCKED_MESSAGE) {
        openNotification('warning', 'Uyarı', message);
      } else {
        openNotification('error', 'Hata', message);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusToggle = async (record: State, nextValue: boolean) => {
    if (typeof record?.id !== 'number') return;
    try {
      setStatusUpdatingId(record.id);
      await setStateActive(record.id, nextValue);
      setData((prev) =>
        prev.filter((item) => {
          if (item.id !== record.id) return true;
          return nextValue === isActiveFilter;
        }),
      );
      openNotification('success', 'Başarılı', nextValue ? 'İl aktif edildi' : 'İl pasif edildi');
    } catch (error) {
      const message = extractApiErrorMessage(error, 'Durum güncellenemedi');
      openNotification('error', 'Hata', message);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const columns = [
    {
      title: 'İl Kodu',
      dataIndex: 'stateCode',
      width: 140,
      sorter: (a: State, b: State) =>
        String(a?.stateCode ?? '').localeCompare(String(b?.stateCode ?? ''), 'tr', { numeric: true, sensitivity: 'base' }),
      defaultSortOrder: 'ascend' as const,
    },
    {
      title: 'İl',
      dataIndex: 'name',
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      width: 140,
      render: (value: boolean, record: State) => (
        <Switch
          checked={!!value}
          checkedChildren='Aktif'
          unCheckedChildren='Pasif'
          onChange={(checked) => handleStatusToggle(record, checked)}
          loading={statusUpdatingId === record.id}
        />
      ),
    },
    {
      title: 'İşlemler',
      width: 260,
      render: (_: unknown, record: State) => (
        <Space>
          <Button size='small' onClick={() => navigate(`/settings/districts?stateId=${record.id}&active=${isActiveFilter}`)}>
            İlçeleri Aç
          </Button>
          <Tooltip title='Düzenle'>
            <Button
              icon={<img src={PenIcon} alt='pen' className='icon' />}
              shape='circle'
              className='icon-button'
              onClick={() => {
                setEditingId(record.id);
                setOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm title='Sil?' onConfirm={() => handleDelete(record.id)}>
            <Tooltip title='Sil'>
              <Button
                icon={<img src={TrashIcon} alt='trash' className='icon' />}
                shape='circle'
                className='icon-button ml-12px'
                loading={deletingId === record.id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        className='br-30px kontrolx-card'
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Tabs
              activeKey={statusTab}
              onChange={(key) => {
                const nextStatus: StatusTabKey = key === 'passive' ? 'passive' : 'active';
                setStatusTab(nextStatus);
                syncSearchParams(nextStatus);
              }}
              className='custom-tabs'
            >
              <Tabs.TabPane
                key='active'
                tab={<Button type={statusTab === 'active' ? 'primary' : 'default'} className='d-button tab-button'>Aktif İl Listesi</Button>}
              />
              <Tabs.TabPane
                key='passive'
                tab={<Button type={statusTab === 'passive' ? 'primary' : 'default'} className='d-button tab-button'>Pasif İl Listesi</Button>}
              />
            </Tabs>
            <Text type='secondary' style={{ fontWeight: 500, marginTop: 24 }}>
              <strong>({filteredData.length})</strong> kayıt listelendi
            </Text>
          </div>
        }
        extra={
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 220 }}
              value={searchInput}
              onChange={(event) => {
                const value = event.target.value || '';
                setSearchInput(value);
                if (value.length >= 2) {
                  debouncedApplySearch(value);
                } else if (!value.length) {
                  debouncedApplySearch('');
                }
              }}
            />
            <Button type='primary' onClick={() => { setEditingId(undefined); setOpen(true); }}>
              Ekle
            </Button>
          </Space>
        }
      >
        <Table
          rowKey='id'
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
          }}
          onChange={(nextPagination) => {
            setPagination({
              current: nextPagination.current ?? 1,
              pageSize: nextPagination.pageSize ?? DEFAULT_PAGE_SIZE,
            });
          }}
        />
      </Card>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        destroyOnClose
        width={520}
        title={editingId ? 'İl Düzenle' : 'İl Ekle'}
      >
        <StateForm
          id={editingId}
          onSuccess={() => {
            setOpen(false);
            fetchData();
          }}
        />
      </Drawer>
    </>
  );
}
